/**
 * Section 05 — Alerts.
 *
 * The signal triage page. Where alerts go when they arrive, the dedup
 * window, BUY/SHORT/SELL/COVER semantics, CONFIRMED=Y vs N, renotify
 * rules, bulk actions, and the difference between Recent / Parked /
 * Acknowledged.
 */
import React from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, List, ListItem, ListItemText } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import GuideCallout from '../GuideCallout';
import GuideExample from '../GuideExample';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_alerts: GuideSection = {
  id: 'alerts',
  label: 'Alerts',
  group: 'pages',
  icon: <NotificationsIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Alerts</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        The full table of every alert the platform has ever received — webhook hits, strategy emits,
        manual creations. The Dashboard's Recent Alerts panel is the last 24 hours; this page is the
        complete archive with filter + bulk actions.
      </Typography>

      <H id="price-alerts">Manual price alerts</H>
      <P>
        Beyond strategy/webhook signals, you can set your own <strong>price alert</strong> — click <strong>Price
        alert</strong> in the Alerts toolbar (or the price-alert button on the Edge AI symbol overview), pick a symbol,
        choose <em>crosses above</em> / <em>crosses below</em>, and a level. A bounded background monitor checks live
        prices every ~30s; when the level is crossed the alert is marked TRIGGERED and you're notified in-app (the header
        bell) and on Telegram. It then appears in the list like any other alert.
      </P>

      <H id="filters">Filtering, auto-refresh &amp; per-row chart</H>
      <P>
        The filter bar above the table narrows the archive on several independent axes — combine them freely:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Action</strong> — BUY / SHORT / SELL / COVER / SELL_FLAT / COVER_FLAT / NOTIFY (info-only).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Origin</strong> — TradingView webhook (TV) vs an in-house Scheduled Scan (SCAN), read from the alert's payload.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Strength</strong> — High / Medium / Low.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Status</strong> — Pending / Triggered / Processed-Parked (ACKNOWLEDGED) / Expired.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Symbol</strong> + a <strong>Load window</strong> chip strip (All / 1d / 2d / … / 15d) and a single-date picker that overrides the window for one specific day.</>} />
        </ListItem>
      </List>
      <P>
        The page <strong>auto-refreshes</strong> so new alerts surface without a manual reload, and the footer reads
        <em> "Showing N of M loaded"</em> (the filtered count vs everything pulled). Each row carries an inline
        <strong> mini-chart</strong> of the symbol around the signal bar plus hover <strong>tooltips</strong>, so you can
        judge a signal in context without leaving the page.
      </P>

      <H id="action-codes">Action codes — what each one means</H>
      <P>
        Six verbs an alert can carry. The first four are the canonical short-list; the FLAT variants are
        for explicit "close all positions of this kind" semantics.
      </P>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Means</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Resulting broker order</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell><strong>BUY</strong></TableCell><TableCell>Open a long position.</TableCell><TableCell>Market or limit BUY.</TableCell></TableRow>
          <TableRow><TableCell><strong>SHORT</strong></TableCell><TableCell>Open a short position.</TableCell><TableCell>Market or limit SELL (with broker's sell-to-open semantics).</TableCell></TableRow>
          <TableRow><TableCell><strong>SELL</strong></TableCell><TableCell>Close a long position.</TableCell><TableCell>SELL of the open long qty.</TableCell></TableRow>
          <TableRow><TableCell><strong>COVER</strong></TableCell><TableCell>Close a short position.</TableCell><TableCell>BUY-to-cover of the open short qty.</TableCell></TableRow>
          <TableRow><TableCell><strong>SELL_FLAT</strong></TableCell><TableCell>Close ALL long positions of this symbol.</TableCell><TableCell>SELL of the aggregate long qty across positions.</TableCell></TableRow>
          <TableRow><TableCell><strong>COVER_FLAT</strong></TableCell><TableCell>Close ALL short positions of this symbol.</TableCell><TableCell>BUY-to-cover of the aggregate short qty.</TableCell></TableRow>
          <TableRow><TableCell><strong>NOTIFY</strong></TableCell><TableCell>Information-only signal — raises an alert / Telegram but is never a trade instruction.</TableCell><TableCell>None — never places an order.</TableCell></TableRow>
        </TableBody>
      </Table>
      <GuideCallout kind="info">
        The platform <strong>suppresses</strong> SELL / COVER / *_FLAT alerts when there's no open position
        of that kind — the webhook returns 200 with <code>skipped: true</code> and a clear message. This
        avoids accidental new opposite-side positions from misfired exits.
      </GuideCallout>

      <H id="confirmed">CONFIRMED=Y vs N</H>
      <P>
        TradingView Pine scripts often emit two alerts for the same setup:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>CONFIRMED=N</strong> — "this is what I'm seeing right now mid-bar". Useful for early-warning notification but can repaint if the bar reverses before close.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>CONFIRMED=Y</strong> — "the bar has closed; this signal is locked in". Never repaints.</>} />
        </ListItem>
      </List>
      <P>
        Auto Trade <strong>only fires on CONFIRMED=Y</strong>. Manually executing a CONFIRMED=N alert is
        allowed but is your call — you're trading the preview, not the verdict.
      </P>

      <H id="dedup">De-duplication</H>
      <P>
        The in-house scans emit at most one signal per symbol per <strong>candle</strong> (per-candle dedup), so
        a scan that re-runs several times inside the same bar won't spray repeats. Genuine repeats are recorded
        but kept out of the actionable flow — no extra speech alert, no Telegram chime, no second broker order.
        Exit alerts with no matching open position are likewise suppressed (see the callout above).
      </P>

      <H id="renotify">ReNotify</H>
      <P>
        To be reminded about an alert later, use <strong>ReNotify</strong> on a <em>parked</em> alert
        (Dashboard → Parked Alerts): schedule a re-alert <strong>At Price</strong> (when the live price crosses
        your level) or <strong>After Time</strong> (a delay, with a live countdown). When it fires, the same
        alert returns to Recent Alerts (status TRIGGERED) with a Telegram message — no new alert is created.
        Setting a new ReNotify replaces the previous one. See the Dashboard → Parked Alerts section for the
        full behaviour.
      </P>

      <H id="bulk">Bulk actions</H>
      <P>
        Tick the checkbox on rows (or the header box to select all) to enable bulk actions in the toolbar:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Bulk status change</strong> — set the selected alerts to a chosen status (e.g. <em>Processed</em> = ACKNOWLEDGED, which the UI labels "PROCESSED") without executing them; counts as "seen / handled".</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Bulk delete</strong> — permanently remove the selected alert rows (with a confirm). The single-row <strong>Park</strong> action moves an alert to the Dashboard's Parked Alerts for later review.</>} />
        </ListItem>
      </List>
      <GuideCallout kind="warning">
        Bulk-execute is intentionally NOT offered. Each Execute is a real broker order; we make you click
        each one to force a deliberate choice.
      </GuideCallout>

      <H id="execute-quantity-sizing">How quantity is sized when you click Execute</H>
      <P>
        <strong>R118c — uniform manual-execute sizing.</strong> Every "Execute" click in the app (this
        page's Execute / One-Click on each row, Dashboard Recent Alerts / Parked Alerts Execute, Charts
        page BUY / SHORT, Dashboard Price Level Watch auto-fire, Pending Entry Watch fire) is routed
        through one shared sizing helper so quantity behaves identically across pages and Auto-Trade
        on/off makes no difference to manual paths.
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Total target qty (Q)</strong> = the same formula Auto-Trade uses: <em>Investment Amount × Leverage ÷ (price × lotSize)</em> from the Instruments page. Any qty hint on the alert / webhook is intentionally ignored — Settings drive sizing.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Each click executes one leg</strong> of <em>Settings → Entry Splitting (N)</em>, using the chart's documented doubling rule (e.g. N=3 → ¼, ¼, ½). After the first execute the cap is frozen on the position so a later Settings change cannot reshape it.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>After N legs are used</strong>, further Execute clicks for that (symbol, side) are rejected with "All N splits already executed — close the position to start a new cycle". The counter resets naturally when the position closes.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Portfolio Reserve %</strong> is checked too — if free margin is below the threshold the order is rejected before any broker call (no partial fills, no "tried but failed" half-state).</>} />
        </ListItem>
      </List>

      <H id="test-webhook">Test webhook button</H>
      <P>
        Top-right of the Alerts page. Fires a synthetic alert against your own webhook URL using your
        per-user secret. Useful for verifying TradingView setup before saving a real alert. Sends:
      </P>
      <Box component="pre" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem', p: 1.25, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 0.75, my: 1 }}>{`{
  "ticker": "BTCUSD",
  "action": "BUY",
  "price": 80000,
  "strategy": "TEST"
}`}</Box>
      <P>
        The alert appears in Recent Alerts with source=TEST. Delete it after verification.
      </P>

      <H id="audit">Audit / log integration</H>
      <P>
        Every alert ever received — including DUPLICATEs, FAILEDs, suppressed exits — is captured in
        <OpenInApp to="/logs" label="Logs" />. Filter by category=webhook for the raw webhook payload trail,
        or by category=alert for the parsed-and-routed alert lifecycle.
      </P>

      <GuideExample title="Reading a typical alert row">
        Row: <code>09:35:12 · BTCUSD · BUY · ₹80,012 · HIGH · CONFIRMED=Y · Sniper · ACK → Trade #1247</code><br />
        Read: at 09:35:12 the Sniper engine emitted a BUY signal on BTCUSD at ₹80,012 with HIGH strength,
        confirmed on bar close. You acknowledged it (clicked Execute), and the resulting position is Trade
        #1247 — click the link to open the Trades page filtered to that row.
      </GuideExample>
    </>
  ),
  searchIndex: [
    { heading: 'Manual price alerts', anchor: 'price-alerts', body: 'price alert notify when symbol crosses above below level threshold telegram bell manual' },
    { heading: 'Filtering & per-row chart', anchor: 'filters',   body: 'filter action origin strength status symbol load window date auto refresh per row mini chart tooltip showing n of m notify' },
    { heading: 'Action codes',          anchor: 'action-codes',  body: 'action buy short sell cover sell_flat cover_flat notify info only' },
    { heading: 'CONFIRMED=Y vs N',      anchor: 'confirmed',     body: 'confirmed y n bar close preview repaint auto trade' },
    { heading: 'De-duplication',        anchor: 'dedup',         body: 'dedup duplicate per candle suppress exit no position' },
    { heading: 'ReNotify',              anchor: 'renotify',      body: 'renotify at price after time parked alert countdown triggered' },
    { heading: 'Bulk actions',          anchor: 'bulk',          body: 'bulk acknowledge discard export csv' },
    { heading: 'Test webhook button',   anchor: 'test-webhook',  body: 'test webhook synthetic alert verification' },
    { heading: 'Audit / log',           anchor: 'audit',         body: 'audit log webhook alert lifecycle' },
  ],
};

export default Section_alerts;
